// src/pages/Dashboard.js - Versión simplificada usando DashboardSelector
import React, { useState } from 'react';
import {
  Container,
  Box,
  Alert,
  Button,
  Typography,
  AppBar,
  Toolbar,
  IconButton,
  Chip
} from '@mui/material';
import {
  Settings as SettingsIcon,
  BugReport as BugReportIcon
} from '@mui/icons-material';

import { useAuth } from '../context/AuthContext';
import useRoleTesting from '../hooks/useRoleTesting';
import RoleSwitcherSimple from '../components/dev/RoleSwitcherSimple';
import { DashboardSelector } from '../components/dashboard/DashboardSelector';

// Mock hooks para datos (mantener los existentes)
const useMockOrganizerData = () => ({
  dashboardData: {
    totalEvents: 8,
    activeEvents: 3,
    totalParticipants: 245,
    totalRevenue: 15800,
    successRate: 94,
    completedEvents: 5,
    upcomingEvents: 2,
    events: [
      { id: 1, name: 'Maratón Ciudad', sport: 'Atletismo', category: 'Elite', registered: 450, capacity: 500, date: '2024-12-20', time: '07:00', status: 'registration_open' },
      { id: 2, name: 'Copa Juvenil', sport: 'Natación', category: 'Junior', registered: 120, capacity: 150, date: '2024-12-25', time: '09:00', status: 'published' },
      { id: 3, name: 'Triatlón Regional', sport: 'Triatlón', category: 'Open', registered: 85, capacity: 100, date: '2025-01-15', time: '06:30', status: 'registration_open' }
    ],
    participants: [
      { id: 1, name: 'Ana García', email: 'ana@email.com', event: 'Maratón Ciudad', status: 'approved', registrationDate: '2024-12-09', category: 'Elite' },
      { id: 2, name: 'Carlos López', email: 'carlos@email.com', event: 'Copa Juvenil', status: 'pending', registrationDate: '2024-12-09', category: 'Junior' },
      { id: 3, name: 'María Rodríguez', email: 'maria@email.com', event: 'Triatlón Regional', status: 'approved', registrationDate: '2024-12-08', category: 'Open' }
    ],
    revenue: { 
      total: 15800, 
      registrations: 8500, 
      sponsorships: 5000, 
      merchandise: 2300,
      growth: '12'
    }
  }, 
  loading: false 
});

const useMockAdminData = () => ({ 
  dashboardData: {
    stats: { totalUsers: 1250, activeEvents: 45 },
    users: [],
    events: [],
    revenue: {},
    systemHealth: {},
    activeUsers: {},
    analytics: {},
    recentActivity: []
  }, 
  loading: false 
});

const Dashboard = () => {
  const { user: authUser } = useAuth();
  const { 
    user, 
    currentRole, 
    testingMode, 
    isUsingTestRole, 
    setTestingRole, 
    resetToRealRole, 
    realRole 
  } = useRoleTesting();
  
  // Estado para manejar cambios en widgets
  const [widgetChanges, setWidgetChanges] = useState({});
  
  // Datos mock
  const adminDataHook = useMockAdminData();
  const organizerDataHook = useMockOrganizerData();
  
  // Rol efectivo (real o de testing)
  const effectiveRole = isUsingTestRole ? currentRole : (authUser?.role || currentRole);

  const handleWidgetsChange = (role, newWidgets) => {
    setWidgetChanges(prev => ({
      ...prev,
      [role]: newWidgets
    }));
    
    // Guardar en localStorage
    localStorage.setItem(`dashboard-layout-${role}`, JSON.stringify(newWidgets));
    console.log(`Widgets actualizados para ${role}:`, newWidgets);
  };

  const getRoleDisplayName = (role) => {
    const roleNames = {
      admin: 'Administrador',
      administrador: 'Administrador',
      organizador: 'Organizador', 
      cronometrador: 'Cronometrador',
      entrenador: 'Entrenador',
      atleta: 'Atleta',
      juez: 'Juez',
      transmision: 'Transmisión'
    };
    return roleNames[role] || role;
  };

  // Preparar datos para pasar a los dashboards específicos
  const dashboardData = {
    admin: adminDataHook.dashboardData,
    organizador: organizerDataHook.dashboardData,
    entrenador: null,
    atleta: null,
    cronometrador: null
  };

  return (
    <Box sx={{ flexGrow: 1, bgcolor: 'background.default', minHeight: '100vh' }}>
      {/* Header opcional */}
      <AppBar 
        position="static" 
        elevation={0} 
        sx={{ 
          bgcolor: 'background.paper', 
          borderBottom: 1, 
          borderColor: 'divider' 
        }}
      >
        <Toolbar>
          <Typography variant="h6" sx={{ flexGrow: 1, color: 'text.primary' }}>
            🏃‍♂️ SportsPlatform
          </Typography>
          
          {/* Switcher de roles para desarrollo */}
          {testingMode && (
            <Box sx={{ mr: 2 }}>
              <RoleSwitcherSimple
                currentRole={effectiveRole}
                onRoleChange={setTestingRole}
                showInProduction={true}
              />
            </Box>
          )}
          
          <Chip 
            label={getRoleDisplayName(effectiveRole)} 
            size="small" 
            color="primary" 
            variant="outlined"
            sx={{ mr: 2 }} 
          />
          
          {isUsingTestRole && (
            <Chip 
              label="Demo" 
              size="small" 
              color="warning" 
              variant="filled"
              icon={<BugReportIcon />}
              sx={{ mr: 2 }} 
            />
          )}
          
          <IconButton color="inherit">
            <SettingsIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      {/* Alerta de modo testing */}
      {isUsingTestRole && (
        <Container maxWidth="xl">
          <Alert 
            severity="info" 
            sx={{ mt: 2 }}
            action={
              <Button color="inherit" size="small" onClick={resetToRealRole}>
                Volver a mi rol
              </Button>
            }
          >
            <Typography variant="body2">
              <strong>Modo Demo:</strong> Estás viendo el dashboard como "{getRoleDisplayName(effectiveRole)}".
              {realRole && ` Tu rol real es "${getRoleDisplayName(realRole)}".`}
            </Typography>
          </Alert>
        </Container>
      )}

      {/* Dashboard dinámico basado en rol */}
      <DashboardSelector
        user={user || authUser}
        userRole={effectiveRole}
        onWidgetsChange={(widgets) => handleWidgetsChange(effectiveRole, widgets)}
        dashboardData={dashboardData[effectiveRole]}
        loading={
          (effectiveRole === 'admin' && adminDataHook.loading) || 
          (effectiveRole === 'organizador' && organizerDataHook.loading)
        }
      />
    </Box>
  );
};

export default Dashboard;